// Radar de producto (PRD I.1–I.4): dolores del mercado según las reseñas analizadas.
// Solo datos reales (radar_data, radar_suggestions). Con menos de 30 negocios analizados en un
// giro no se estiman porcentajes ni mercado: se marca "muestra chica" y cuántos faltan.
import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Minus, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { MobilePage } from "../MobilePage";
import { OfferForm, type OfferFormTarget } from "../pipeline/OfferForm";
import { ReviewsButton, useReviewThemes } from "../reviews/reviews";
import { offerColor, useOffers } from "../useOffers";

const sb = () => getSupabaseClient();
const nf = (n: number) => Math.round(n).toLocaleString("es-MX");

/* Ideas para los dolores que hoy no resuelve ningún producto (textos base del prototipo) */
export const IDEAS: Record<
  string,
  {
    tipo: "producto_nuevo" | "integracion";
    base: string | null;
    nombre: string;
    desc: string;
  }
> = {
  reputacion: {
    tipo: "producto_nuevo",
    base: null,
    nombre: "Respuestas a reseñas con IA",
    desc: "Responde cada reseña en minutos con el tono del negocio y avisa al dueño de las negativas. Aplica a casi todos los giros.",
  },
  atencion: {
    tipo: "producto_nuevo",
    base: null,
    nombre: "Pulso: encuesta QR y alerta al dueño",
    desc: "El cliente califica en el momento con un QR; si está molesto, el dueño recibe alerta antes de que escriba una reseña.",
  },
  espera: {
    tipo: "integracion",
    base: "mooi",
    nombre: "Fila virtual y pedido anticipado",
    desc: "El cliente se forma o pide desde su celular y recibe aviso por WhatsApp cuando es su turno. Puede nacer como módulo de MOOI.",
  },
  facturacion: {
    tipo: "integracion",
    base: "storcky",
    nombre: "Autofactura con QR en el ticket",
    desc: "El cliente se factura solo escaneando el ticket; el negocio deja de perseguir datos fiscales.",
  },
  pagos: {
    tipo: "integracion",
    base: "storcky",
    nombre: "Cobro con liga de pago y terminal",
    desc: "Cobra con tarjeta o transferencia desde el punto de venta y concilia solo.",
  },
  calidad: {
    tipo: "integracion",
    base: "kelmo",
    nombre: "Checklists de calidad con evidencia",
    desc: "Revisiones por turno con foto y hora; el dueño ve qué falló y quién lo firmó.",
  },
  limpieza: {
    tipo: "integracion",
    base: "kelmo",
    nombre: "Bitácoras de limpieza con foto",
    desc: "Rondas de limpieza con foto y hora, visibles para inspecciones y clientes.",
  },
};
const STAGES = [
  ["idea", "Idea"],
  ["validando", "Validando"],
  ["construyendo", "Construyendo"],
  ["lanzado", "Lanzado"],
] as const;

type Universe = {
  sector: string;
  total: number;
  emp6: number;
  qro: number;
  qro_emp6: number;
};
type Giro = { sector: string; n: number; propias: number; resp_baja: number };
type Cell = {
  sector: string;
  k: string;
  menciona: number;
  dolor: number;
  neg: number;
};
type Radar = {
  minimo: number;
  negocios: number;
  propias: number;
  universo: Universe[];
  giros: Giro[];
  celdas: Cell[];
  demanda: { slug: string; sector: string; n: number }[];
  otros: {
    texto: string;
    n: number;
    negativas: number;
    negocios: number;
    ejemplo: string | null;
  }[];
};
type Suggest = {
  por_empresa: number;
  competidores: number;
  cupo: { quedan: number; usadas: number; mes: number };
  giros: {
    sector: string;
    qro_fit: number;
    analizados: number;
    faltan: number;
    empresas: {
      id: number;
      nombre: string;
      municipio: string;
      thermo: number;
      fits: string[];
    }[];
  }[];
};
type Idea = {
  id: number;
  theme_key: string | null;
  other_text: string | null;
  name: string;
  kind: "producto_nuevo" | "integracion";
  base_offer_id: number | null;
  description: string | null;
  stage: string;
  notes: string | null;
  offer_id: number | null;
  validation: { entrevistas: number; landing: boolean; preventas: number };
};

const useRadar = () =>
  useQuery({
    queryKey: ["radar_data"],
    queryFn: async () => {
      const { data, error } = await sb().rpc("radar_data");
      if (error) throw error;
      return data as Radar;
    },
  });
const useIdeas = () =>
  useQuery({
    queryKey: ["product_ideas"],
    queryFn: async () =>
      ((await sb().from("product_ideas").select("*").order("created_at"))
        .data ?? []) as Idea[],
  });

/** Estimación solo con giros confiables (30+ negocios analizados). */
const estimate = (R: Radar, per: (s: string) => number) => {
  const U = new Map(R.universo.map((u) => [u.sector, u]));
  let est = 0,
    qro = 0,
    ok = true;
  const faltan: string[] = [];
  for (const g of R.giros) {
    const hits = per(g.sector);
    if (!hits) continue;
    if (g.n < R.minimo) {
      ok = false;
      faltan.push(`${g.sector} (faltan ${R.minimo - g.n})`);
      continue;
    }
    const u = U.get(g.sector);
    if (u) {
      est += (hits / g.n) * u.emp6;
      qro += (hits / g.n) * u.qro_emp6;
    }
  }
  return { ok, est, qro, faltan };
};

const Panel = ({
  title,
  cap,
  children,
  className,
}: {
  title: string;
  cap?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) => (
  <Card className={cn("gap-2 p-4", className)}>
    <h2 className="text-base font-extrabold">{title}</h2>
    {cap && <p className="text-xs text-muted-foreground">{cap}</p>}
    {children}
  </Card>
);
const Chica = ({ faltan }: { faltan: number }) => (
  <span
    className="rounded-full border border-dashed px-1.5 text-[10.5px] font-semibold text-muted-foreground"
    title={`Hacen falta ${faltan} negocios analizados más para que el dato sea confiable`}
  >
    muestra chica · faltan {faltan}
  </span>
);

export const RadarPage = () => {
  const { data: R } = useRadar();
  const { data: T } = useReviewThemes();
  const { data: offers } = useOffers();
  const { data: ideas, refetch: refetchIdeas } = useIdeas();
  const { data: sug } = useQuery({
    queryKey: ["radar_suggestions"],
    queryFn: async () =>
      (await sb().rpc("radar_suggestions", { p_per: 5 })).data as Suggest,
  });
  const notify = useNotify();
  const qc = useQueryClient();
  const [offerForm, setOfferForm] = useState<OfferFormTarget | null>(null);
  const lab = (k: string) => T?.get(k)?.label ?? k;
  const offerName = (slug: string) =>
    offers?.find((o) => o.slug === slug)?.name ?? slug;

  if (!R)
    return (
      <MobilePage title="Radar de producto">
        <p className="p-4 text-sm">Cargando…</p>
      </MobilePage>
    );

  const G = new Map(R.giros.map((g) => [g.sector, g]));
  // Giros del mapa: los que tienen datos primero, luego los más grandes del directorio
  const rows = [
    ...R.giros.map((g) => g.sector),
    ...R.universo
      .map((u) => u.sector)
      .filter((s) => !G.has(s) && s !== "Otro" && s !== "Sector Público"),
  ].slice(0, 13);
  const U = new Map(R.universo.map((u) => [u.sector, u]));
  const themes = [...new Set(R.celdas.map((c) => c.k))].sort(
    (a, b) =>
      R.celdas.filter((c) => c.k === b).reduce((x, c) => x + c.dolor, 0) -
      R.celdas.filter((c) => c.k === a).reduce((x, c) => x + c.dolor, 0),
  );
  const cell = (s: string, k: string) =>
    R.celdas.find((c) => c.sector === s && c.k === k);

  // I.2 Oportunidades: dolores sin solución propia + "Respuestas a reseñas con IA"
  const gaps = Object.keys(IDEAS)
    .filter((k) => k === "reputacion" || !(T?.get(k)?.offer_slugs.length ?? 0))
    .map((k) => {
      const per =
        k === "reputacion"
          ? (s: string) => G.get(s)?.resp_baja ?? 0
          : (s: string) => cell(s, k)?.dolor ?? 0;
      const hits = R.giros.reduce((a, g) => a + per(g.sector), 0);
      const cs = R.celdas.filter((c) => c.k === k && c.dolor > 0);
      const neg =
        k === "reputacion"
          ? 0
          : cs.length
            ? Math.round(cs.reduce((a, c) => a + c.neg, 0) / cs.length)
            : 0;
      const e = estimate(R, per);
      const where = R.giros
        .filter((g) => per(g.sector) > 0)
        .sort((a, b) => per(b.sector) - per(a.sector));
      // mercado × severidad × crecimiento (sin tendencia todavía: crecimiento = 1)
      const sc = e.ok
        ? (e.est * Math.max(neg, 30)) / 100
        : hits * Math.max(neg, 30);
      return { k, per, hits, neg, e, where, sc };
    })
    .filter((g) => g.hits > 0)
    .sort((a, b) => +b.e.ok - +a.e.ok || b.sc - a.sc);

  const addIdea = async (row: Partial<Idea>) => {
    const { error } = await sb().from("product_ideas").insert(row);
    if (error) return notify(error.message, { type: "error" });
    notify("Pasó a ideas de producto", { type: "success" });
    refetchIdeas();
  };
  const ideaFromGap = (k: string) => {
    const I = IDEAS[k];
    return addIdea({
      theme_key: k === "reputacion" ? null : k,
      name: I.nombre,
      kind: I.tipo,
      base_offer_id: I.base
        ? (offers?.find((o) => o.slug === I.base)?.id ?? null)
        : null,
      description: I.desc,
    });
  };
  const inIdeas = (name: string) => ideas?.some((i) => i.name === name);

  const covered = (offers ?? []).map((o) => {
    const per = (s: string) =>
      R.demanda
        .filter((d) => d.slug === o.slug && d.sector === s)
        .reduce((a, d) => a + d.n, 0);
    const hits = R.giros.reduce((a, g) => a + per(g.sector), 0);
    return {
      o,
      hits,
      e: estimate(R, per),
      themes: [...(T?.values() ?? [])].filter((t) =>
        t.offer_slugs.includes(o.slug),
      ),
    };
  });

  return (
    <MobilePage title="Radar de producto">
      <div className="flex max-w-6xl flex-col gap-4">
        <div className="max-md:hidden">
          <h1 className="text-2xl font-extrabold tracking-tight">
            Radar de producto
          </h1>
          <p className="text-sm text-muted-foreground">
            Dolores del mercado según las reseñas analizadas · para decidir qué
            construir después
          </p>
        </div>

        <Card className="gap-1 border-dashed p-4 text-sm">
          <b>Cómo leerlo</b>
          <span className="text-muted-foreground">
            Todo es real. Hoy hay <b>{R.negocios}</b> negocio
            {R.negocios === 1 ? "" : "s"} con reseñas analizadas ({R.propias} de
            tu directorio y {R.negocios - R.propias} competidores) en{" "}
            {R.giros.length} giro
            {R.giros.length === 1 ? "" : "s"}. El universo por giro es tu
            directorio DENUE (empresas con 6 o más empleados). Para dar
            porcentajes y estimar el mercado hacen falta{" "}
            <b>{R.minimo} negocios analizados por giro</b>; mientras no se
            llega, se muestra el conteo real y “muestra chica”, sin extrapolar.
          </span>
        </Card>

        <Panel
          title="Mapa de dolores por giro"
          cap="Negocios del giro cuyas reseñas muestran la queja (2 o más menciones y 25 % o más negativas). Con 30+ analizados se ve el %."
        >
          {!themes.length ? (
            <p className="text-sm text-muted-foreground">
              Sin datos todavía: analiza reseñas de algunas empresas (abajo te
              sugiero cuáles).
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-xs"
                style={{ minWidth: 160 + themes.length * 70 }}
              >
                <thead>
                  <tr>
                    <th className="p-1 text-left">Giro</th>
                    {themes.map((k) => (
                      <th
                        key={k}
                        className="p-1 align-bottom text-[10.5px] leading-tight font-semibold"
                      >
                        {lab(k)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => {
                    const g = G.get(s);
                    const ok = !!g && g.n >= R.minimo;
                    return (
                      <tr key={s} className="border-t">
                        <td className="p-1 whitespace-nowrap">
                          <b>{s}</b>
                          <div className="text-[11px] text-muted-foreground">
                            {nf(U.get(s)?.emp6 ?? 0)} con 6+ empleados ·{" "}
                            {g ? `${g.n} analizados` : "sin datos todavía"}
                          </div>
                          {g && !ok && <Chica faltan={R.minimo - g.n} />}
                        </td>
                        {themes.map((k) => {
                          const c = cell(s, k);
                          if (!g || !c) return <td key={k} className="p-1" />;
                          const pct = c.dolor / g.n;
                          return (
                            <td key={k} className="p-0.5">
                              <div
                                title={`${s} · ${lab(k)}: ${c.dolor} de ${g.n} negocios con la queja; ${c.neg} % de sus menciones son negativas`}
                                className={cn(
                                  "grid h-8 place-items-center rounded-md text-[11px] font-bold",
                                  !ok && "border border-dashed",
                                )}
                                style={{
                                  background:
                                    ok && c.dolor
                                      ? `color-mix(in oklab, var(--critical) ${Math.round(15 + pct * 70)}%, transparent)`
                                      : undefined,
                                }}
                              >
                                {ok
                                  ? `${Math.round(pct * 100)} %`
                                  : `${c.dolor} de ${g.n}`}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <div className="grid gap-4 lg:grid-cols-[7fr_5fr]">
          <Panel
            title="Oportunidades de producto (lo que hoy no resuelves)"
            cap="Ordenadas por tamaño del mercado × qué tanto molesta × si va creciendo. Sin 30 negocios por giro, el orden es por negocios con el dolor."
          >
            {!gaps.length && (
              <p className="text-sm text-muted-foreground">
                Sin datos todavía.
              </p>
            )}
            {gaps.map((g, i) => {
              const I = IDEAS[g.k];
              return (
                <div
                  key={g.k}
                  className="flex flex-col gap-1.5 rounded-lg border p-3 text-sm"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <b className="flex-1">
                      {i + 1}. {I.nombre}
                    </b>
                    <span className="rounded-full border px-2 text-xs">
                      {I.tipo === "integracion"
                        ? `Integración a ${offerName(I.base!)}`
                        : "Producto nuevo"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Dolor: </span>
                    {g.k === "reputacion"
                      ? "El dueño casi no responde sus reseñas (menos de 30 %)"
                      : lab(g.k)}
                    {g.neg
                      ? ` · ${g.neg} % de las menciones del tema son negativas`
                      : ""}
                    <span className="text-muted-foreground">
                      {" "}
                      · tendencia: sin datos todavía
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      Qué construir:{" "}
                    </span>
                    {I.desc}
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      Dónde más duele:{" "}
                    </span>
                    {g.where
                      .map((w) => `${w.sector} (${g.per(w.sector)} de ${w.n})`)
                      .join(", ")}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Mercado: </span>
                    {g.e.ok ? (
                      <>
                        ≈ {nf(g.e.est)} empresas con 6+ empleados · ≈{" "}
                        {nf(g.e.qro)} en Querétaro
                      </>
                    ) : (
                      <>
                        {g.hits} negocio{g.hits === 1 ? "" : "s"} con el dolor
                        entre los analizados · sin estimado:{" "}
                        <Chica
                          faltan={Math.max(
                            ...g.where.map((w) => R.minimo - w.n),
                          )}
                        />
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {g.k !== "reputacion" && (
                      <Button asChild size="sm" variant="outline">
                        <Link
                          to={`/directory?filter=${encodeURIComponent(JSON.stringify({ _quejas: [g.k] }))}`}
                        >
                          Ver empresas con este dolor
                        </Link>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      disabled={inIdeas(I.nombre)}
                      onClick={() => ideaFromGap(g.k)}
                    >
                      {inIdeas(I.nombre)
                        ? "Ya está en ideas"
                        : "Pasar a ideas de producto"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </Panel>

          <div className="flex flex-col gap-4">
            <Panel
              title="Demanda para tus productos actuales"
              cap="Negocios analizados con al menos un dolor que ya resuelve cada producto. El estimado del mercado sale solo con 30+ por giro."
            >
              <ul className="flex flex-col gap-1.5 text-sm">
                {covered.map(({ o, hits, e, themes: th }) => (
                  <li
                    key={o.id}
                    className="flex flex-wrap items-center gap-2"
                    title={th.map((t) => t.label).join(", ")}
                  >
                    <span
                      className="size-2.5 rounded-full"
                      style={{ background: offerColor(o) }}
                    />
                    <b className="w-28 truncate">{o.name}</b>
                    <span className="text-muted-foreground">
                      {!th.length
                        ? "sin temas ligados en el catálogo"
                        : !hits
                          ? "sin datos todavía"
                          : e.ok
                            ? `≈ ${nf(e.est)} empresas (≈ ${nf(e.qro)} en Qro.)`
                            : `${hits} de ${R.negocios} analizados · muestra chica`}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>

            <OtherThemes R={R} onIdea={addIdea} inIdeas={inIdeas} />
          </div>
        </div>

        <Panel
          title="Ideas de producto"
          cap="Valida antes de construir: el dolor tiene que confirmarse en conversaciones reales."
        >
          {!ideas?.length && (
            <p className="text-sm text-muted-foreground">
              Todavía no hay ideas. Pasa una desde las oportunidades.
            </p>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            {ideas?.map((x) => (
              <IdeaCard
                key={x.id}
                x={x}
                onChange={refetchIdeas}
                onBoard={() =>
                  setOfferForm({
                    mode: "create",
                    name: x.name,
                    onCreated: async (id) => {
                      await sb()
                        .from("product_ideas")
                        .update({
                          offer_id: id,
                          updated_at: new Date().toISOString(),
                        })
                        .eq("id", x.id);
                      qc.invalidateQueries({ queryKey: ["product_ideas"] });
                    },
                  })
                }
              />
            ))}
          </div>
        </Panel>

        {sug && <Suggestions s={sug} />}
      </div>
      <OfferForm target={offerForm} onClose={() => setOfferForm(null)} />
    </MobilePage>
  );
};

/* ------------------------------------------------------------- temas "otro" */
const OtherThemes = ({
  R,
  onIdea,
  inIdeas,
}: {
  R: Radar;
  onIdea: (row: Partial<Idea>) => void;
  inIdeas: (n: string) => boolean | undefined;
}) => {
  const notify = useNotify();
  const qc = useQueryClient();
  const [label, setLabel] = useState<Record<string, string>>({});
  const act = async (fn: string, args: Record<string, unknown>, ok: string) => {
    const { error } = await sb().rpc(fn, args);
    if (error) return notify(error.message, { type: "error" });
    notify(ok, { type: "success" });
    qc.invalidateQueries({ queryKey: ["radar_data"] });
    qc.invalidateQueries({ queryKey: ["review_themes"] });
  };
  const cap = (t: string) => t[0].toUpperCase() + t.slice(1);
  return (
    <Panel
      title="Temas nuevos por decidir"
      cap="Quejas que la IA no pudo poner en el catálogo (“otro”). Si una se repite, vuélvela tema: sus reseñas se re-etiquetan sin gastar IA ni SerpApi."
    >
      {!R.otros.length && (
        <p className="text-sm text-muted-foreground">
          No hay temas pendientes.
        </p>
      )}
      <ul className="flex flex-col gap-2 text-sm">
        {R.otros.map((o) => (
          <li
            key={o.texto}
            className="flex flex-col gap-1 rounded-lg border p-2"
          >
            <div>
              <b>{cap(o.texto)}</b>{" "}
              <span className="text-muted-foreground">
                · {o.n} mención{o.n === 1 ? "" : "es"} en {o.negocios} negocio
                {o.negocios === 1 ? "" : "s"}
              </span>
            </div>
            {o.ejemplo && (
              <i className="text-xs text-muted-foreground">«{o.ejemplo}»</i>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
              <input
                className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1 text-xs"
                value={label[o.texto] ?? cap(o.texto)}
                onChange={(e) =>
                  setLabel({ ...label, [o.texto]: e.target.value })
                }
                aria-label="Nombre del tema"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  act(
                    "radar_promote_other",
                    {
                      p_text: o.texto,
                      p_label: label[o.texto] ?? cap(o.texto),
                    },
                    "Ahora es un tema del catálogo",
                  )
                }
              >
                Volver tema
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  act("radar_ignore_other", { p_text: o.texto }, "Ignorado")
                }
              >
                Ignorar
              </Button>
              {!inIdeas(cap(o.texto)) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    onIdea({
                      name: cap(o.texto),
                      other_text: o.texto,
                      description: `Queja en reseñas: «${o.texto}»`,
                    })
                  }
                >
                  A ideas
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
};

/* ------------------------------------------------------------- ideas (I.4) */
const IdeaCard = ({
  x,
  onChange,
  onBoard,
}: {
  x: Idea;
  onChange: () => void;
  onBoard: () => void;
}) => {
  const notify = useNotify();
  const { data: offers } = useOffers();
  const save = async (patch: Partial<Idea>) => {
    const { error } = await sb()
      .from("product_ideas")
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq("id", x.id);
    if (error) return notify(error.message, { type: "error" });
    onChange();
  };
  const v = x.validation;
  const setV = (p: Partial<Idea["validation"]>) =>
    save({ validation: { ...v, ...p } });
  const Counter = ({
    k,
    max,
  }: {
    k: "entrevistas" | "preventas";
    max: number;
  }) => (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        className="rounded border p-0.5"
        aria-label="Menos"
        onClick={() => setV({ [k]: Math.max(0, v[k] - 1) })}
      >
        <Minus className="size-3" />
      </button>
      <b className={cn(v[k] >= max && "text-[var(--good)]")}>
        {v[k]} / {max}
      </b>
      <button
        type="button"
        className="rounded border p-0.5"
        aria-label="Más"
        onClick={() => setV({ [k]: Math.min(max, v[k] + 1) })}
      >
        <Plus className="size-3" />
      </button>
    </span>
  );
  const board = offers?.find((o) => o.id === x.offer_id);
  return (
    <div className="flex flex-col gap-2 rounded-lg border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <b className="flex-1">{x.name}</b>
        <select
          className="rounded-md border bg-background px-2 py-1 text-xs"
          value={x.stage}
          onChange={(e) => save({ stage: e.target.value })}
          aria-label="Etapa"
        >
          {STAGES.map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
        <button
          type="button"
          aria-label="Quitar idea"
          className="rounded p-1 hover:bg-secondary"
          onClick={async () => {
            if (!window.confirm(`¿Quitar la idea «${x.name}»?`)) return;
            await sb().from("product_ideas").delete().eq("id", x.id);
            onChange();
          }}
        >
          <X className="size-4" />
        </button>
      </div>
      {x.description && (
        <p className="text-xs text-muted-foreground">{x.description}</p>
      )}
      <ul className="flex flex-col gap-1.5">
        <li className="flex flex-wrap items-center justify-between gap-2">
          <span>
            1. Entrevista a 15 empresas con el dolor
            {x.theme_key && (
              <>
                {" "}
                (
                <Link
                  className="underline"
                  to={`/directory?filter=${encodeURIComponent(JSON.stringify({ _quejas: [x.theme_key] }))}`}
                >
                  ver lista
                </Link>
                )
              </>
            )}
            . Meta: 10 de 15 confirman que les duele.
          </span>
          <Counter k="entrevistas" max={15} />
        </li>
        <li className="flex flex-wrap items-center justify-between gap-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={v.landing}
              onChange={(e) => setV({ landing: e.target.checked })}
            />
            2. Landing de prueba con precio (meta: 5 % de las visitas deja sus
            datos)
          </label>
        </li>
        <li className="flex flex-wrap items-center justify-between gap-2">
          <span>
            3. Preventa a 5 clientes antes de programar una línea de código
          </span>
          <Counter k="preventas" max={5} />
        </li>
      </ul>
      <textarea
        className="rounded-md border bg-background px-2 py-1 text-xs"
        placeholder="Notas: qué dijeron en las entrevistas, precio probado…"
        defaultValue={x.notes ?? ""}
        onBlur={(e) =>
          e.target.value !== (x.notes ?? "") && save({ notes: e.target.value })
        }
      />
      <div>
        {board ? (
          <Button asChild size="sm" variant="outline">
            <Link to={`/board/${board.id}`}>Abrir tablero de {board.name}</Link>
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={onBoard}>
            <Plus /> Crear tablero de producto nuevo
          </Button>
        )}
      </div>
    </div>
  );
};

/* --------------------------------------------- qué analizar (no gasta nada) */
const Suggestions = ({ s }: { s: Suggest }) => {
  const perCo = s.por_empresa;
  const q = s.cupo.quedan;
  return (
    <Panel
      title="Qué empresas conviene analizar para que el Radar sirva"
      cap={
        <>
          Te quedan <b>{q}</b> búsquedas de SerpApi este mes (de {s.cupo.mes};
          se detiene al 90 %). Una empresa nueva gasta {perCo} búsquedas (≈{" "}
          {Math.floor(q / perCo)} empresas). “Comparar con competidores” gasta{" "}
          {s.competidores} y suma 3 negocios del mismo giro: es la forma más
          barata de llenar un giro (≈ {Math.floor(q / s.competidores) * 3}{" "}
          negocios). Nada se gasta hasta que tú lo confirmes.
        </>
      }
    >
      <div className="grid gap-3 md:grid-cols-2">
        {s.giros.map((g) => (
          <div
            key={g.sector}
            className="flex flex-col gap-1.5 rounded-lg border p-3 text-sm"
          >
            <div className="flex flex-wrap items-center gap-2">
              <b className="flex-1">{g.sector}</b>
              <span className="text-xs text-muted-foreground">
                {g.analizados} de 30 · faltan {g.faltan}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              {nf(g.qro_fit)} empresas tibias o más en Querétaro que encajan en
              tus productos. Las más calientes sin reseñas:
            </span>
            <ul className="text-xs">
              {g.empresas.map((e) => (
                <li key={e.id}>
                  <Link className="underline" to={`/directory/${e.id}/show`}>
                    {e.nombre}
                  </Link>{" "}
                  · {e.municipio} · termómetro {e.thermo}
                </li>
              ))}
            </ul>
            {!!g.empresas.length && (
              <div>
                <ReviewsButton
                  ids={g.empresas.map((e) => e.id)}
                  label={`Analizar estas ${g.empresas.length}`}
                  variant="outline"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
};
